import { useMemo } from "react"
import PropTypes from "prop-types"
import { chain, map, find, keys, omitBy } from "lodash"
import { Paper } from "@mui/material"
import Table from "components/material/table"
import { DATASET, UUID, ROLE } from "helpers/shapes"
import { makeDatasetElementRender, makeSourcesElementRender } from "helpers/datasets"
import UpsellTabs, { UPSELL_TABS_SHAPE } from "components/material/upsell-tabs"

export default function MemberDatasetEntitlements({ account, role, memberId, entitlements, datasets, update, tabs }) {
  const hasWriteAccess = role === "admin"

  const optionLabels = useMemo(() => chain(datasets)
    .orderBy("name", "asc")
    .reduce((datasetsById, dataset) => ({
      ...datasetsById,
      [dataset.id]: dataset.name,
    }), {})
    .value(),
  [datasets])

  // Only direct entitlements are important here
  const availableOptionLabels = useMemo(() =>
    omitBy(optionLabels, (_, id) => find(entitlements, entitlement => entitlement.id === id && entitlement.direct)),
  [entitlements, optionLabels])

  const addForm = useMemo(() => {
    if (!hasWriteAccess) { return null }

    function onAdd(data, cb) {
      const query = `mutation ($memberIds: [IdOrEmail]){
        ${map(data.datasets, (datasetId, index) => `dataset${index}: grantDatasetAccess(id: "${datasetId}", memberIds: $memberIds) { id }`)}
      }`

      const variables = { memberIds: [memberId] }

      update(query, variables, account, {
        reloadData: ["memberDatasetEntitlements", "userDatasets", "models", "datasetEntitlements"],
      }, cb)
    }

    return {
      title: "Grant Dataset Access",
      submitText: "Grant access",
      track: {
        type: "dataset-entitlements-grant-datasets-member",
        payload: ({ datasets }) => ({
          datasets,
          memberId,
        }),
      },
      onSubmit: onAdd,
      fields:
      [{
        id: "datasets",
        label: "Datasets",
        type: "multi",
        options: keys(availableOptionLabels),
        optionLabels: availableOptionLabels,
        validations: {
          isRequired: true,
        },
      }],
    }
  }, [hasWriteAccess, availableOptionLabels, memberId, update, account])

  const deleteForm = useMemo(() => {
    if (!hasWriteAccess) { return null }

    function onDelete(data, cb) {
      const query = `mutation ($memberIds: [IdOrEmail]){
        ${map(data, (dataset, index) => `dataset${index}: revokeDatasetAccess(id: "${dataset.id}", memberIds: $memberIds) { id }`)}
      }`

      const variables = { memberIds: [memberId] }

      update(query, variables, account, {
        reloadData: ["memberDatasetEntitlements", "userDatasets", "models", "datasetEntitlements"],
      }, cb)
    }

    return {
      type: "dataset-entitlement",
      track: {
        type: "dataset-entitlements-revoke-datasets-member",
        payload: (dataset) => ({
          dataset: dataset.id,
          memberId,
        }),
      },
      // Only direct entitlements can be removed
      isEnabled: (dataset) => dataset.direct && hasWriteAccess,
      renderItem: dataset => <li key={dataset.id}>{dataset.name}</li>,
      onSubmit: onDelete,
    }
  }, [account, hasWriteAccess, memberId, update])

  // This will always be true because we cannot reach this page via the UI if entitlements are not enabled
  const entitlementsEnabled = true
  // Same functions for all table elements
  const getNameElement = makeDatasetElementRender(account, role, entitlementsEnabled, 400)
  const getSourcesElement = makeSourcesElementRender(account)

  return <div className="modules_member-dataset-entitlements">
    <UpsellTabs id="member-dataset-entitlements" tabs={tabs} />
    <Paper className="content" square>
      <Table
        title="Member dataset entitlements"
        idField="id"
        addForm={addForm}
        editForm={null}
        deleteForm={deleteForm}
        data={entitlements}
        columns={[{
          name: "Name",
          id: "name",
          render: getNameElement,
        },
        {
          name: "Sources",
          id: "sources",
          render: getSourcesElement,
        }]}
      />
    </Paper>
  </div>
}

MemberDatasetEntitlements.propTypes = {
  account: PropTypes.string.isRequired,
  memberId: UUID,
  userId: UUID,
  role: ROLE.isRequired,
  entitlements: PropTypes.arrayOf(DATASET),
  datasets: PropTypes.arrayOf(DATASET).isRequired,
  update: PropTypes.func.isRequired,
  tabs: UPSELL_TABS_SHAPE,
}
